import type { ChatMessage, ToolCall } from "../types/chat";

const TOOL_LABEL: Record<string, string> = {
  run_equity_analysis: "Equity Agent",
  run_visual_analysis: "VLM Agent",
  get_cell_risk: "Cell Risk lookup",
  get_weather_trend: "Weather lookup",
  get_nearby_firms: "FIRMS lookup",
  lookup_cell_demographics: "lookup demographics",
  get_stratum_metrics: "lookup stratum recall",
  flag_disparity: "test disparity",
  get_tile_metadata: "tile metadata",
  get_firms_summary: "FIRMS summary",
  check_imagery_reliability: "imagery reliability",
};

function describeResult(name: string, result: ToolCall["result"]): string {
  if (!result) return "";
  if ("error" in result && result.error) {
    return ` — error: ${String(result.error).slice(0, 120)}`;
  }
  if (name === "run_equity_analysis") {
    const r = result as { svi_quintile?: string; stratum_recall?: number; is_flagged?: boolean; delta_eo_global?: number };
    const parts: string[] = [];
    if (r.svi_quintile) parts.push(`SVI ${r.svi_quintile}`);
    if (r.stratum_recall != null) parts.push(`recall ${(r.stratum_recall * 100).toFixed(1)}%`);
    if (r.delta_eo_global != null) parts.push(`ΔEO ${r.delta_eo_global.toFixed(3)}`);
    if (r.is_flagged) parts.push("flagged");
    return parts.length ? ` — ${parts.join(", ")}` : "";
  }
  if (name === "run_visual_analysis") {
    const r = result as { fuel_state?: string; wui_indicator?: string; active_fire_visible?: boolean; insufficient_imagery?: boolean; rgb_capture_date?: string };
    if (r.insufficient_imagery) return " — imagery insufficient";
    const parts: string[] = [];
    if (r.rgb_capture_date) parts.push(`tile ${r.rgb_capture_date}`);
    if (r.fuel_state) parts.push(`fuel ${r.fuel_state}`);
    if (r.wui_indicator) parts.push(`WUI ${r.wui_indicator}`);
    if (r.active_fire_visible) parts.push("active fire");
    return parts.length ? ` — ${parts.join(", ")}` : "";
  }
  if (name === "get_cell_risk") {
    const r = result as { p_hat?: number; p_tilde?: number };
    return ` — p̂=${r.p_hat?.toFixed?.(3) ?? "?"} p̃=${r.p_tilde?.toFixed?.(3) ?? "?"}`;
  }
  if (name === "get_nearby_firms") {
    const r = result as { count?: number; nearest_km?: number };
    return ` — ${r.count ?? 0} detections, nearest ${r.nearest_km ?? "?"} km`;
  }
  return "";
}

function formatElapsed(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  return s < 10 ? `${s.toFixed(1)}s` : `${Math.round(s)}s`;
}

function elapsedTag(c: ToolCall): string {
  if (c.startedAt == null) return "";
  const end = c.endedAt ?? Date.now();
  return ` (${formatElapsed(end - c.startedAt)})`;
}

function renderToolLines(calls: ToolCall[] | undefined, indent = 0): string[] {
  if (!calls || calls.length === 0) return [];
  const out: string[] = [];
  for (const c of calls) {
    const pad = " ".repeat(indent * 2);
    const label = TOOL_LABEL[c.name] ?? c.name;
    const status = c.status === "pending" ? " [pending]" : "";
    out.push(`${pad}- ${label}${status}${elapsedTag(c)}${describeResult(c.name, c.result)}`);
    const inner = (c.result?.tool_call_trace ?? []) as ToolCall[];
    if (inner.length) out.push(...renderToolLines(inner, indent + 1));
  }
  return out;
}

export function buildTranscript(opts: {
  cellId: string;
  tickId: string | null;
  messages: ChatMessage[];
}): string {
  const { cellId, tickId, messages } = opts;
  const lines: string[] = [];
  lines.push("FireFair chat transcript");
  lines.push(`Cell:   ${cellId}`);
  if (tickId) lines.push(`Tick:   ${tickId}`);
  lines.push(`Saved:  ${new Date().toISOString()}`);
  lines.push("");
  lines.push("=".repeat(60));
  lines.push("");

  for (const msg of messages) {
    if (msg.role === "user") {
      lines.push("[ You ]");
      lines.push(msg.content || "(empty)");
    } else {
      lines.push("[ Report Agent ]");
      const toolLines = renderToolLines(msg.toolCalls);
      if (toolLines.length) {
        lines.push("(tools)");
        lines.push(...toolLines);
        lines.push("");
      }
      lines.push(msg.content || "(no reply)");
    }
    lines.push("");
    lines.push("-".repeat(60));
    lines.push("");
  }
  return lines.join("\n");
}

export function downloadTranscript(opts: {
  cellId: string;
  tickId: string | null;
  messages: ChatMessage[];
}): void {
  const text = buildTranscript(opts);
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  const safeCell = opts.cellId.replace(/[^A-Za-z0-9_.-]/g, "_");
  const a = document.createElement("a");
  a.href = url;
  a.download = `firefair-chat-${safeCell}-${ts}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // give the browser a moment before revoking
  setTimeout(() => URL.revokeObjectURL(url), 500);
}
